import { LE, LE_CODES } from "@/constants/enums";
import { getElectronicFormRouteName } from "@/helpers/electronicFormHelpers";
import type { StandardFormRouteNames } from "@/views/electronicForm/electronicForm";
import { createWorksheetChildRoutes } from "@/views/electronicForm/worksheets/worksheetRoutes";
import { EntityAudit } from "@modernary/mui/components/audit";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import { RouterView, type RouteRecordRaw, type RouterOptions } from "vue-router";
import { createDocumentChildRoutes } from "../documents/documentRoutes";

// Reusable HOC for dynamic imports with props
const withProps = (importFn: () => Promise<any>, defaultProps: Record<string, any> = {}) => {
  return {
    component: importFn,
    props: defaultProps,
  };
};

// Troubleshoot your routes using this - https://paths.esm.dev

const stationarySourcePermittingForms: Array<
  keyof Pick<
    typeof LE_CODES.ElectronicForm,
    | "ef001"
    | "ef002"
    | "ef003"
    | "ef004"
    | "ef005"
    | "ef006"
    | "ef007"
    | "ef008"
    | "ef009"
    | "ef010"
    | "ef011"
    | "ef012"
    | "ef013"
    | "ef014"
    | "ef015"
    | "ef016"
    | "ef017"
    | "ef018"
    | "ef019"
    | "ef020"
    | "ef021"
    | "ef022"
    | "ef023"
    | "ef024"
    | "ef025"
    | "ef026"
    | "ef027"
    | "ef028"
    | "ef029"
    | "ef030"
    | "ef031"
    | "ef032"
    | "ef033"
    | "ef034"
    | "ef035"
    | "ef036"
    | "ef037"
  >
> = [
  "ef001",
  "ef002",
  "ef003",
  "ef004",
  "ef005",
  "ef006",
  "ef007",
  "ef008",
  "ef009",
  "ef010",
  "ef011",
  "ef012",
  "ef013",
  "ef014",
  "ef015",
  "ef016",
  "ef017",
  "ef018",
  "ef019",
  "ef020",
  "ef021",
  "ef022",
  "ef023",
  "ef024",
  "ef025",
  "ef026",
  "ef027",
  "ef028",
  "ef029",
  "ef030",
  "ef031",
  "ef032",
  "ef033",
  "ef034",
  "ef035",
  "ef036",
  "ef037",
] as const;

const nameGenerator = (acc: any, form: (typeof stationarySourcePermittingForms)[number]) => {
  if (!acc[form]) acc[form] = {};

  const current = acc[form];
  current.new = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "New");
  current.view = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "View");
  current.edit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Edit");
  current.base = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Base");
  current.audit = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Audit");

  current.initiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "InitiateSubmission");
  current.submissionAgreementForPdf = getElectronicFormRouteName(
    LE_CODES.ElectronicForm[form],
    "SubmissionAgreementForPdf",
  );
  current.reviewSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "ReviewSubmission");
  current.repudiateSubmission = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "RepudiateSubmission");

  current.worksheets = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Worksheets");
  current.documents = getElectronicFormRouteName(LE_CODES.ElectronicForm[form], "Documents");

  return acc;
};

// Explicitly defined names go here
const staticNames = {
  stationarySourcePermitting: "ElectronicForm.StationarySourcePermitting",
  stationarySourcePermittingList: "ElectronicForm.StationarySourcePermittingList",
  stationarySourcePermittingSelect: "ElectronicForm.StationarySourcePermittingSelect",

  // ef001: {
  //   initiateSubmission: "ElectronicForm.ef001InitiateSubmission",
  //   reviewSubmission: "ElectronicForm.ef001ReviewSubmission",
  //   repudiateSubmission: "ElectronicForm.ef001RepudiateSubmission",
  // },
};

type StaticStationarySourceNames = typeof staticNames;

type GeneratedStationarySourceNames = {
  [K in (typeof stationarySourcePermittingForms)[number]]: StandardFormRouteNames;
};

// Combined type for all route names -
type StationarySourceRouteNames = StaticStationarySourceNames & GeneratedStationarySourceNames;

// Dynamically generate all common names, starting with staticNames as the initial value
const names = stationarySourcePermittingForms.reduce(nameGenerator, staticNames) as StationarySourceRouteNames;

const additionalChildRoutes: { [key: string]: RouterOptions["routes"] } = {
  // routes beyond the standard predefined routes should be added here.
  // the key should be the form code, and the value should be an array of routes.
  // ef001: [
  //   {
  //     path: "initiateSubmission",
  //     name: names.ef001.initiateSubmission,
  //     meta: { title: "Submission Agreement", isForm: true },
  //     ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
  //       agreement: StationarySourceStandardAgreementTemplate,
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  //   {
  //     path: "reviewSubmission",
  //     name: names.ef001.reviewSubmission,
  //     meta: { title: "Submission Review", isForm: true },
  //     ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
  //       review: StationarySourceStandardReviewTemplate,
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  //   {
  //     path: "repudiateSubmission",
  //     name: names.ef001.repudiateSubmission,
  //     meta: { title: "Submission Repudiation", isForm: true },
  //     ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
  //       formRouteNames: names.ef001,
  //     }),
  //   },
  // ],
};

const createRoutes = () => {
  const routes: RouterOptions["routes"] = stationarySourcePermittingForms.map((form) => {
    const formRouteNames = names[form];
    const result: RouteRecordRaw = {
      path: LE_CODES.ElectronicForm[form],
      name: LE_CODES.ElectronicForm[form],
      component: {
        render: () => <RouterView />,
      },
      children: [
        {
          path: "new",
          component: () =>
            import(`@/views/electronicForm/stationarySourcePermitting/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.new },
          // hard code the paths based on what form you're making...
          children: [
            {
              path: ":id(\\d+)?/:typeMurn?",
              name: formRouteNames.new,
              meta: { title: "New Form", isNew: true },
              component: () =>
                import(`@/views/electronicForm/stationarySourcePermitting/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
          ],
        },
        {
          path: ":id(\\d+)",
          name: formRouteNames.base,
          component: () =>
            import(`@/views/electronicForm/stationarySourcePermitting/${LE_CODES.ElectronicForm[form]}/Base.vue`),
          redirect: { name: formRouteNames.view },
          children: [
            {
              path: "view",
              name: formRouteNames.view,
              meta: { title: "Form View" },
              component: () =>
                import(`@/views/electronicForm/stationarySourcePermitting/${LE_CODES.ElectronicForm[form]}/View.vue`),
            },
            {
              path: "edit",
              name: formRouteNames.edit,
              meta: { title: "Form Edit", isForm: true },
              component: () =>
                import(`@/views/electronicForm/stationarySourcePermitting/${LE_CODES.ElectronicForm[form]}/Edit.vue`),
            },
            {
              path: "activity",
              name: formRouteNames.audit,
              meta: { title: "Form Audit Activity" },
              component: {
                render: () => <EntityAudit entityType="ElectronicForm" />,
              },
            },
            {
              path: "initiateSubmission",
              name: formRouteNames.initiateSubmission,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              // Mirrors the initiateSubmission route for PDF generation.
              path: "submissionAgreementForPdf",
              name: formRouteNames.submissionAgreementForPdf,
              meta: { title: "Submission Agreement", isForm: true },
              ...withProps(() => import("@/components/submission/InitiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
                isSubmissionAgreementForPdf: true,
              }),
            },
            {
              path: "reviewSubmission",
              name: formRouteNames.reviewSubmission,
              meta: { title: "Submission Review", isForm: true },
              ...withProps(() => import("@/components/submission/ReviewSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            {
              path: "repudiateSubmission",
              name: formRouteNames.repudiateSubmission,
              meta: { title: "Submission Repudiation", isForm: true },
              ...withProps(() => import("@/components/submission/RepudiateSubmission.vue"), {
                formCode: form,
                formRouteNames,
              }),
            },
            ...createWorksheetChildRoutes(formRouteNames),
            ...createDocumentChildRoutes(formRouteNames),
            ...(additionalChildRoutes[form] ? additionalChildRoutes[form] : []),
          ],
        },
      ],
    };
    return result;
  });
  return routes;
};

export { names as stationarySourcePermittingRouteNames };

const stationarySourcePermittingRoutes: RouterOptions["routes"] = [
  {
    path: "/stationarySourcePermitting",
    name: names.stationarySourcePermitting,
    component: {
      render: () => <RouterView />,
    },
    redirect: { name: names.stationarySourcePermittingList },
    children: [
      {
        path: "list",
        name: names.stationarySourcePermittingList,
        meta: {
          title: "Stationary Source Forms",
          lookupGroups: ["ElectronicForm", "ElectronicFormType", "ElectronicFormStatus"],
        },
        // props: (route) => ({
        //   filterMurns: route.params.filterMurns,
        // }),
        // beforeEnter: async (to, from, next) => {
        //   try {
        //     const formTypeLookups = await getLookupsFromLookupGroup("ElectronicForm");

        //     to.params.filterMurns = formTypeLookups._items
        //       .filter((lookup) =>
        //         lookup.metadata?.filtersOn?.groupMurn.values.includes(
        //           LE.ElectronicFormGroup.StationarySourcePermitting,
        //         ),
        //       )
        //       .map((x) => x.murn);

        //     next();
        //   } catch (error) {
        //     next({ name: "errorRoute" }); //TODO:
        //   }
        // },
        component: () => import("@/views/electronicForm/stationarySourcePermitting/StationarySourceList.vue"),
      },

      {
        path: "formSelect",
        name: names.stationarySourcePermittingSelect,
        meta: { title: "Select Stationary Source Form", isNew: true },
        component: () => import("@/views/electronicForm/stationarySourcePermitting/StationarySourceSelect.vue"),
      },
      ...createRoutes(),
      // {
      //   path: LE_CODES.ElectronicForm.ef070,
      //   name: LE_CODES.ElectronicForm.ef070,
      //   component: {
      //     render: () => <RouterView />,
      //   },
      //   children: [
      //     {
      //       path: "new",
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef070New },
      //       // hard code the paths based on what form you're making...
      //       children: [
      //         {
      //           path: "",
      //           name: names.ef070New,
      //           meta: { title: "New Form", isNew: true },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Edit.vue"),
      //         },
      //       ],
      //     },
      //     {
      //       path: ":id(\\d+)",
      //       name: names.ef070Base,
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef070View },
      //       children: [
      //         {
      //           path: "view",
      //           name: names.ef070View,
      //           meta: { title: "Form View" },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/View.vue"),
      //         },
      //         {
      //           path: "edit",
      //           name: names.ef070Edit,
      //           meta: { title: "Form Edit", isForm: true },
      //           component: () =>
      //             import("@/views/electronicForm/dustControlPermitting/070_DustControlOperatingPermit/Edit.vue"),
      //         },

      //         {
      //           path: "activity",
      //           name: names.ef070Audit,
      //           meta: { title: "Form Audit Activity" },
      //           component: {
      //             render: () => <EntityAudit entityType="ElectronicForm" />,
      //           },
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   path: LE_CODES.ElectronicForm.ef071,
      //   name: LE_CODES.ElectronicForm.ef071,
      //   component: {
      //     render: () => <RouterView />,
      //   },
      //   children: [
      //     {
      //       path: "new",
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef071New },
      //       // hard code the paths based on what form you're making...
      //       children: [
      //         {
      //           path: "",
      //           name: names.ef071New,
      //           meta: { title: "New Form", isNew: true },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Edit.vue"
      //             ),
      //         },
      //       ],
      //     },
      //     {
      //       path: ":id(\\d+)",
      //       name: names.ef071Base,
      //       component: () =>
      //         import("@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Base.vue"),
      //       redirect: { name: names.ef071View },
      //       children: [
      //         {
      //           path: "view",
      //           name: names.ef071View,
      //           meta: { title: "Form View" },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/View.vue"
      //             ),
      //         },
      //         {
      //           path: "edit",
      //           name: names.ef071Edit,
      //           meta: { title: "Form Edit", isForm: true },
      //           component: () =>
      //             import(
      //               "@/views/electronicForm/dustControlPermitting/071_ImplosionDustControlOperatingPermit/Edit.vue"
      //             ),
      //         },

      //         {
      //           path: "activity",
      //           name: names.ef071Audit,
      //           meta: { title: "Form Audit Activity" },
      //           component: {
      //             render: () => <EntityAudit entityType="ElectronicForm" />,
      //           },
      //         },
      //       ],
      //     },
      //   ],
      // },
    ],
  },
];
export default stationarySourcePermittingRoutes;
